import * as THREE from 'three';

export interface ParticleConfig {
  count: number;
  lifetime: number;
  speed: number;
  speedVariance?: number;
  size: number;
  sizeVariance?: number;
  color: THREE.Color;
  colorEnd?: THREE.Color;
  gravity?: number;
  fadeOut?: boolean;
  shrink?: boolean;
  spread?: number; // Cone angle in radians
}

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  colorStart: THREE.Color;
  colorEnd: THREE.Color;
}

export class ParticleEmitter {
  private scene: THREE.Scene;
  private particles: Particle[] = [];
  private geometry: THREE.BufferGeometry;
  private material: THREE.PointsMaterial;
  private points: THREE.Points;
  private config: ParticleConfig;
  private isActive: boolean = false;

  constructor(scene: THREE.Scene, config: ParticleConfig) {
    this.scene = scene;
    this.config = config;

    // Create geometry with positions and colors
    this.geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(config.count * 3);
    const colors = new Float32Array(config.count * 3);
    const sizes = new Float32Array(config.count);

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    // Create material
    this.material = new THREE.PointsMaterial({
      size: config.size,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
  }

  public emit(position: THREE.Vector3, direction?: THREE.Vector3): void {
    this.isActive = true;

    if (!this.points.parent) {
      this.scene.add(this.points);
    }

    const dir = direction?.clone().normalize() || new THREE.Vector3(0, 1, 0);

    for (let i = 0; i < this.config.count; i++) {
      // Calculate velocity with spread
      const velocity = dir.clone();

      if (this.config.spread) {
        // Add random spread
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.random() * this.config.spread;
        const spreadDir = new THREE.Vector3(
          Math.sin(phi) * Math.cos(theta),
          Math.cos(phi),
          Math.sin(phi) * Math.sin(theta)
        );

        // Rotate spread direction to align with main direction
        const quat = new THREE.Quaternion();
        quat.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        spreadDir.applyQuaternion(quat);

        velocity.copy(spreadDir);
      }

      const speedVar = this.config.speedVariance || 0;
      const speed = this.config.speed + (Math.random() - 0.5) * 2 * speedVar;
      velocity.multiplyScalar(speed);

      const sizeVar = this.config.sizeVariance || 0;
      const size = this.config.size + (Math.random() - 0.5) * 2 * sizeVar;

      this.particles.push({
        position: position.clone(),
        velocity,
        life: this.config.lifetime,
        maxLife: this.config.lifetime,
        size,
        colorStart: this.config.color.clone(),
        colorEnd: this.config.colorEnd?.clone() || this.config.color.clone(),
      });
    }
  }

  public update(dt: number): void {
    if (!this.isActive) return;

    const positions = this.geometry.attributes.position.array as Float32Array;
    const colors = this.geometry.attributes.color.array as Float32Array;
    const sizes = this.geometry.attributes.size.array as Float32Array;

    // Update existing particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      // Apply gravity
      if (this.config.gravity) {
        p.velocity.y -= this.config.gravity * dt;
      }

      // Update position
      p.position.addScaledVector(p.velocity, dt);
    }

    // Update buffer attributes
    for (let i = 0; i < this.config.count; i++) {
      if (i < this.particles.length) {
        const p = this.particles[i];
        const lifeRatio = p.life / p.maxLife;

        positions[i * 3] = p.position.x;
        positions[i * 3 + 1] = p.position.y;
        positions[i * 3 + 2] = p.position.z;

        // Interpolate color
        const color = p.colorStart.clone().lerp(p.colorEnd, 1 - lifeRatio);
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;

        // Size with optional shrink
        let size = p.size;
        if (this.config.shrink) {
          size *= lifeRatio;
        }
        sizes[i] = size;
      } else {
        // Hide unused particles
        positions[i * 3] = 0;
        positions[i * 3 + 1] = -1000;
        positions[i * 3 + 2] = 0;
        sizes[i] = 0;
      }
    }

    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
    this.geometry.attributes.size.needsUpdate = true;

    // Update opacity for fade out
    if (this.config.fadeOut && this.particles.length > 0) {
      const avgLife = this.particles.reduce((sum, p) => sum + p.life / p.maxLife, 0) / this.particles.length;
      this.material.opacity = avgLife;
    }

    // Deactivate when all particles are gone
    if (this.particles.length === 0) {
      this.isActive = false;
    }
  }

  public isFinished(): boolean {
    return !this.isActive && this.particles.length === 0;
  }

  public destroy(): void {
    this.scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}

// Pre-configured effect factories
export class EffectFactory {
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public createExplosion(position: THREE.Vector3, color: THREE.Color = new THREE.Color(0xff6600)): ParticleEmitter {
    const emitter = new ParticleEmitter(this.scene, {
      count: 30,
      lifetime: 0.6,
      speed: 8,
      speedVariance: 4,
      size: 0.4,
      sizeVariance: 0.2,
      color: color,
      colorEnd: new THREE.Color(0x220000),
      gravity: 10,
      fadeOut: true,
      shrink: true,
      spread: Math.PI,
    });
    emitter.emit(position);
    return emitter;
  }

  public createSparks(position: THREE.Vector3, direction: THREE.Vector3): ParticleEmitter {
    const emitter = new ParticleEmitter(this.scene, {
      count: 15,
      lifetime: 0.3,
      speed: 12,
      speedVariance: 6,
      size: 0.15,
      sizeVariance: 0.1,
      color: new THREE.Color(0xffff00),
      colorEnd: new THREE.Color(0xff4400),
      gravity: 15,
      fadeOut: true,
      spread: Math.PI / 3,
    });
    emitter.emit(position, direction);
    return emitter;
  }

  public createMuzzleFlash(position: THREE.Vector3, direction: THREE.Vector3, color: THREE.Color = new THREE.Color(0x00ffff)): ParticleEmitter {
    const emitter = new ParticleEmitter(this.scene, {
      count: 8,
      lifetime: 0.08,
      speed: 20,
      speedVariance: 5,
      size: 0.3,
      sizeVariance: 0.1,
      color: color,
      colorEnd: new THREE.Color(0xffffff),
      fadeOut: true,
      shrink: true,
      spread: Math.PI / 8,
    });
    emitter.emit(position, direction);
    return emitter;
  }

  public createPickupEffect(position: THREE.Vector3, color: THREE.Color): ParticleEmitter {
    const emitter = new ParticleEmitter(this.scene, {
      count: 20,
      lifetime: 0.5,
      speed: 5,
      speedVariance: 2,
      size: 0.2,
      sizeVariance: 0.1,
      color: color,
      colorEnd: new THREE.Color(0xffffff),
      gravity: -5, // Float upward
      fadeOut: true,
      spread: Math.PI,
    });
    emitter.emit(position);
    return emitter;
  }
}
